import React, { useRef, useCallback } from 'react'

import { Col, Row, Tab, Tooltip } from '@enuygun/design-system'
import { i18n, useTranslation } from 'next-i18next'

import SnapCarousel from 'components/SnapCarousel'
import { useAppState } from 'context/AppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { organizeDataIntoGroups } from 'utils/helper'

import { TravelRoutesDataTR } from './dataTR'
import { TRAVEL_ROUTES_DATA_WINGIE } from './dataWingie'
import * as S from './TravelRoutes.styled'

import { InfoCircleOutline } from '@enuygun/icons'
import { IconWrapper, TooltipText } from 'components/styled'
import { useIsClient } from 'hooks/useIsClient'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

interface TravelRoutesProps {
  res: any
  isTablet?: boolean
}

const getMarketPrefix = (market?: string) => (market === 'tr' ? 'enuygun' : 'wingie')

const getRouteGroupSize = (isMobile: boolean | null | undefined) => (isMobile ? 8 : 4)

const isTravelRoutesRtl = () => {
  const lang = i18n?.language
  const dirLang = lang?.includes('-') ? lang.split('-')[1] : lang
  return i18n?.dir(dirLang) === 'rtl'
}

const wingieGroupKey = (group: Array<{ url?: string; cityName?: string; countryName?: string }>, fallback: string) =>
  group.map((route) => route.url || route.cityName || route.countryName).filter(Boolean).join('-') || fallback

const trGroupKey = (group: Array<{ src?: string; routeName?: string }>, fallback: string) =>
  group.map((route) => route.src || route.routeName).filter(Boolean).join('-') || fallback

const shouldSkipTravelRoutesResponse = (response: any, isWingie: boolean) => {
  if (response?.error && isWingie) return true
  return Object.keys(response).length === 0
}

const buildWingieRouteSections = (response: any, flatData: any, groupSize: number) => {
  const eur = {
    items: organizeDataIntoGroups(response?.popularCrossCities?.eur, groupSize),
    title: flatData?.topDomesticFlight?.title,
    type: 'topDomesticFlights',
  }
  const ww = {
    items: organizeDataIntoGroups(response?.popularCrossCities?.ww, groupSize),
    title: flatData?.TopDestinationsWorldwide?.title,
    type: 'topDestinationsWorldwide',
  }
  const popular = {
    items: organizeDataIntoGroups(response?.popularCountries, groupSize),
    title: flatData?.PopularCountries?.title,
    type: 'popularCountries',
  }
  return { eur, ww, popular }
}

const hasAnyWingieRouteItems = (eur: { items: any[] }, ww: { items: any[] }, popular: { items: any[] }) => {
  return eur.items.length > 0 || ww.items.length > 0 || popular.items.length > 0
}

const buildTRRoutePanels = (flatData: any, groupSize: number) => [
  {
    items: organizeDataIntoGroups(flatData?.flight?.data, groupSize),
    title: flatData?.flight?.title,
    type: 'popularDestinations',
    panelKey: 0,
    testIdAttr: 'data-testid' as const,
  },
  {
    items: organizeDataIntoGroups(flatData?.bus?.data, groupSize),
    title: flatData?.bus?.title,
    type: 'bus',
    panelKey: 1,
    testIdAttr: 'data-testid' as const,
  },
  {
    items: organizeDataIntoGroups(flatData?.otel?.data, groupSize),
    title: flatData?.otel?.title,
    type: 'hotel',
    panelKey: 2,
    testIdAttr: 'dataTestId' as const,
  },
  {
    items: organizeDataIntoGroups(flatData?.carRental?.data, groupSize),
    title: flatData?.carRental?.title,
    type: 'carRental',
    panelKey: 3,
    testIdAttr: 'dataTestId' as const,
  },
  {
    items: organizeDataIntoGroups(flatData?.transfer?.data, groupSize),
    title: flatData?.transfer?.title,
    type: 'transfer',
    panelKey: 4,
    testIdAttr: 'dataTestId' as const,
  },
]

const TravelRoutesHeadingTooltip: React.FC<{
  tooltipText: React.ReactNode
  prefix: string
  isMobile: boolean | null | undefined
  isTablet: boolean | undefined
}> = ({ tooltipText, prefix, isMobile, isTablet }) => {
  if (!tooltipText) return null

  return (
    <span style={{ display: 'inline-block' }} data-testid={`${prefix}-homepage-travel-routes-heading-tooltip`}>
      <Tooltip
        bottomSheet={true}
        title={<div></div>}
        popover={{ width: '350px', popover: { placement: 'bottom' } }}
        button={
          <IconWrapper bgColor={isMobile || isTablet ? '#F8FAF9' : '#fff'} data-testid={`${prefix}-homepage-travel-routes-heading-tooltip-button`}>
            <InfoCircleOutline width={15.6} />
          </IconWrapper>
        }
      >
        <TooltipText data-testid={`${prefix}-homepage-travel-routes-heading-tooltip-text`}>{tooltipText}</TooltipText>
      </Tooltip>
    </span>
  )
}

const calculateOrder = (groupedData: any[][], groupIndex: number, routeIndex: number) => {
  let order = 0
  for (let i = 0; i < groupIndex; i++) {
    order += groupedData[i]?.length || 0
  }
  return order + routeIndex + 1
}

interface WingieRouteLinkProps {
  route: { url: string; cityName?: string; countryName?: string }
  type: string
  popularType: string
  order: number
  subIndex: number
  market?: string
  title: string
  onRouteClick: (url: string, cityName: string | null, countryName: string | null, routeType: string, order: number) => void
}

const WingieRouteLink: React.FC<WingieRouteLinkProps> = ({ route, type, popularType, order, subIndex, market, title, onRouteClick }) => {
  const prefix = getMarketPrefix(market)
  const handleClick = () => onRouteClick(route.url, route.cityName || null, route.countryName || null, type, order)
  const displayName = type === popularType ? route.countryName : route.cityName

  return (
    <li data-testid={`${prefix}-homepage-travel-routes-list-${type}-${subIndex}-item`}>
      <a href={route.url} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${subIndex}-link`} title={title} target="_blank" rel="noreferrer" onClick={handleClick}>
        {displayName}
      </a>
    </li>
  )
}

interface TRRouteLinkProps {
  route: { src: string; routeName: string }
  type: string
  order: number
  subIndex: number
  market?: string
  onRouteClick: (url: string, routeName: string, routeType: string, order: number) => void
  asStyledLink?: boolean
}

const TRRouteLink: React.FC<TRRouteLinkProps> = ({ route, type, order, subIndex, market, onRouteClick, asStyledLink = false }) => {
  const prefix = getMarketPrefix(market)
  const handleClick = () => onRouteClick(route.src, route.routeName, type, order)
  const testId = `${prefix}-homepage-travel-routes-list-${type}-${subIndex}-link`

  return (
    <li data-testid={`${prefix}-homepage-travel-routes-list-${type}-${subIndex}-item`}>
      {asStyledLink ? (
        <S.TravelRoutesLink href={route.src} data-testid={testId} title={route.routeName} target="_blank" rel="noreferrer" onClick={handleClick}>
          {route.routeName}
        </S.TravelRoutesLink>
      ) : (
        <a href={route.src} data-testid={testId} title={route.routeName} target="_blank" rel="noreferrer" onClick={handleClick}>
          {route.routeName}
        </a>
      )}
    </li>
  )
}

interface GetTabContentWingieProps {
  groupedData: any[][]
  type: string
  popularType: string
  isMobile: boolean
  isTablet: boolean
  isRTL: boolean
  market?: string
  t: (key: string, options?: any) => string
  onRouteClick: (url: string, cityName: string | null, countryName: string | null, routeType: string, order: number) => void
}

const GetTabContentWingie: React.FC<GetTabContentWingieProps> = ({ groupedData, type, popularType, isMobile, isTablet, isRTL, market, t, onRouteClick }) => {
  const prefix = getMarketPrefix(market)

  if (!isMobile && !isTablet) {
    return (
      <S.TravelRoutesList as={Row} gutter={0} data-testid={`${prefix}-homepage-travel-routes-list`}>
        {groupedData.map((group, key) => (
          <Col as={S.Ul} style={{ flexGrow: 1 }} key={wingieGroupKey(group, `group-${key}`)} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${key}-column`}>
            {group.map((route, subIndex) => (
              <WingieRouteLink
                key={route.url || route.cityName || route.countryName || `route-${subIndex}`}
                route={route}
                type={type}
                popularType={popularType}
                order={calculateOrder(groupedData, key, subIndex)}
                subIndex={subIndex}
                market={market}
                title={t('travelRoutesTitle', { name: type === popularType ? route.countryName : route.cityName })}
                onRouteClick={onRouteClick}
              />
            ))}
          </Col>
        ))}
      </S.TravelRoutesList>
    )
  }

  return (
    <SnapCarousel
      options={{
        perPage: isTablet ? 1.9 : 1.2,
        gap: 16,
        lastChildFullWidth: false,
      }}
      dataTestId={`${prefix}-homepage-travel-routes-list-${type}-carousel`}
      containerWidth={1120}
      isMobile={!!isMobile}
      isRTL={isRTL}
    >
      {groupedData.map((group, index) => (
        <div key={wingieGroupKey(group, `group-${index}`)} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${index}-container`}>
          {group.map((route, subIndex) => (
            <S.Ul key={route.url || route.cityName || route.countryName || `route-${subIndex}`} dir={isRTL} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${subIndex}-column`}>
              <WingieRouteLink
                route={route}
                type={type}
                popularType={popularType}
                order={calculateOrder(groupedData, index, subIndex)}
                subIndex={subIndex}
                market={market}
                title={t('travelRoutesTitle', { name: type === popularType ? route.countryName : route.cityName })}
                onRouteClick={onRouteClick}
              />
            </S.Ul>
          ))}
        </div>
      ))}
    </SnapCarousel>
  )
}

interface GetTabContentTRProps {
  groupedData: any[][]
  type: string
  isMobile: boolean
  isTablet: boolean
  market?: string
  onRouteClick: (url: string, routeName: string, routeType: string, order: number) => void
}

const GetTabContentTR: React.FC<GetTabContentTRProps> = ({ groupedData, type, isMobile, isTablet, market, onRouteClick }) => {
  const prefix = getMarketPrefix(market)

  if (!isMobile && !isTablet) {
    return (
      <S.TravelRoutesList as={Row} gutter={0} data-testid={`${prefix}-homepage-travel-routes-list`}>
        {groupedData?.map((group, key) => (
          <Col as={S.Ul} style={{ flexGrow: 1 }} key={trGroupKey(group, `group-${key}`)} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${key}-column`}>
            {group.map((route, subIndex) => (
              <TRRouteLink
                key={route.src || route.routeName || `route-${subIndex}`}
                route={route}
                type={type}
                order={calculateOrder(groupedData, key, subIndex)}
                subIndex={subIndex}
                market={market}
                onRouteClick={onRouteClick}
                asStyledLink
              />
            ))}
          </Col>
        ))}
      </S.TravelRoutesList>
    )
  }

  return (
    <SnapCarousel
      options={{
        perPage: isTablet ? 1.9 : 1.2,
        gap: 16,
        lastChildFullWidth: false,
      }}
      dataTestId={`${prefix}-homepage-travel-routes-list-${type}-carousel`}
      containerWidth={1120}
      isMobile={!!isMobile}
    >
      {groupedData.map((group, index) => (
        <S.Ul key={trGroupKey(group, `group-${index}`)} style={{ width: '100%' }} data-testid={`${prefix}-homepage-travel-routes-list-${type}-${index}-column`}>
          {group.map((route, subIndex) => (
            <TRRouteLink
              key={route.src || route.routeName || `route-${subIndex}`}
              route={route}
              type={type}
              order={calculateOrder(groupedData, index, subIndex)}
              subIndex={subIndex}
              market={market}
              onRouteClick={onRouteClick}
            />
          ))}
        </S.Ul>
      ))}
    </SnapCarousel>
  )
}

interface WingieTabPanelsProps {
  tabs: Array<{ data: { items: any[][]; title: string; type: string }; index: number }>
  popularType: string
  isMobile: boolean
  isTablet: boolean
  isRTL: boolean
  isClient: boolean
  market?: string
  t: (key: string, options?: any) => string
  onRouteClick: (url: string, cityName: string | null, countryName: string | null, routeType: string, order: number) => void
}

/** Returns Tab.Panel[] — must be spread/mapped as direct Tab children (not wrapped). */
const renderWingieTabPanels = ({ tabs, popularType, isMobile, isTablet, isRTL, isClient, market, t, onRouteClick }: WingieTabPanelsProps) => {
  const prefix = getMarketPrefix(market)

  return tabs
    .filter(({ data }) => data.items?.length)
    .map(({ data, index }) => (
      <Tab.Panel unmount={!!isClient} title={data.title} panelKey={index} key={index} data-testid={`${prefix}-homepage-travel-routes-list-${data.title}-panel`}>
        <GetTabContentWingie
          groupedData={data.items}
          type={data.type}
          popularType={popularType}
          isMobile={isMobile}
          isTablet={isTablet}
          isRTL={isRTL}
          market={market}
          t={t}
          onRouteClick={onRouteClick}
        />
      </Tab.Panel>
    ))
}

interface TRTabPanelsProps {
  panels: Array<{ items: any[][]; title: string; type: string; panelKey: number; testIdAttr: 'data-testid' | 'dataTestId' }>
  isMobile: boolean
  isTablet: boolean
  isClient: boolean
  market?: string
  onRouteClick: (url: string, routeName: string, routeType: string, order: number) => void
}

const renderTRTabPanels = ({ panels, isMobile, isTablet, isClient, market, onRouteClick }: TRTabPanelsProps) => {
  const prefix = getMarketPrefix(market)

  return panels.map(({ items, title, type, panelKey, testIdAttr }) => {
    const testId = `${prefix}-homepage-travel-routes-list-${type}-panel`
    const testIdProps = testIdAttr === 'dataTestId' ? { dataTestId: testId } : { 'data-testid': testId }

    return (
      <Tab.Panel unmount={!!isClient} title={title} panelKey={panelKey} key={panelKey} {...testIdProps}>
        <GetTabContentTR groupedData={items} type={type} isMobile={isMobile} isTablet={isTablet} market={market} onRouteClick={onRouteClick} />
      </Tab.Panel>
    )
  })
}

const TravelRoutes: React.FC<TravelRoutesProps> = ({ res }) => {
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const { t } = useTranslation('common')
  const { market, language } = useAppState()
  const isClient = useIsClient()

  const travelRoutesContainerRef = useRef<HTMLDivElement>(null)

  const isWingie = market !== 'tr'
  const prefix = getMarketPrefix(market)
  const groupSize = getRouteGroupSize(isMobile)

  const handleWingieRouteClick = useCallback(
    (url: string, cityName: string | null, countryName: string | null, routeType: string, order: number) => {
      const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'click',
        message: 'travel_routes_link_click',
        environment: environment,
        requestId,
        linkUrl: url,
        cityName: cityName,
        countryName: countryName,
        routeType: routeType,
        routeOrder: order,
        isMobile: !!(isMobile || isTablet),
        market: market,
      })
    },
    [isMobile, isTablet, market]
  )

  const handleTRRouteClick = useCallback(
    (url: string, routeName: string, routeType: string, order: number) => {
      const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: 'enuygun-homepage',
        page: 'enuygun-homepage',
        type: 'click',
        message: 'travel_routes_link_click',
        environment: environment,
        requestId,
        linkUrl: url,
        routeName: routeName,
        routeType: routeType,
        routeOrder: order,
        isMobile: !!(isMobile || isTablet),
        market: market,
      })
    },
    [isMobile, isTablet, market]
  )

  if (!res?.[3]) return null

  const $response = res[3]
  if (shouldSkipTravelRoutesResponse($response, isWingie)) return null

  const data = isWingie ? TRAVEL_ROUTES_DATA_WINGIE[language as string] : TravelRoutesDataTR
  const flatData = flattenObjectArray(data)
  const { eur: $eur, ww: $ww, popular: $popular } = buildWingieRouteSections($response, flatData, groupSize)

  if (!hasAnyWingieRouteItems($eur, $ww, $popular)) return null

  const isRTL = isTravelRoutesRtl()
  const tooltipText = t('travelRoutes.headingTooltip', { defaultValue: false })
  const wingieTabs = [
    { data: $eur, index: 0 },
    { data: $ww, index: 1 },
    { data: $popular, index: 2 },
  ]
  const trPanels = buildTRRoutePanels(flatData, groupSize)

  const tabPanels = isWingie
    ? renderWingieTabPanels({
        tabs: wingieTabs,
        popularType: $popular.type,
        isMobile: !!isMobile,
        isTablet: !!isTablet,
        isRTL,
        isClient,
        market,
        t,
        onRouteClick: handleWingieRouteClick,
      })
    : renderTRTabPanels({
        panels: trPanels,
        isMobile: !!isMobile,
        isTablet: !!isTablet,
        isClient,
        market,
        onRouteClick: handleTRRouteClick,
      })

  return (
    <S.TravelRoutesContainer ref={travelRoutesContainerRef} data-testid={`${prefix}-homepage-travel-routes-container`}>
      <S.TravelRoutesHeading data-testid={`${prefix}-homepage-travel-routes-heading`}>
        {t('travelRoutes.heading')}
        <TravelRoutesHeadingTooltip tooltipText={tooltipText} prefix={prefix} isMobile={isMobile} isTablet={isTablet} />
      </S.TravelRoutesHeading>

      <Tab unmount={isClient} dataTestId={`${prefix}-homepage-travel-routes-list-popularDestinations`} {...res} shape="outlined">
        {tabPanels}
      </Tab>
    </S.TravelRoutesContainer>
  )
}

export default TravelRoutes

function flattenObjectArray(arr) {
  if (!arr) return {}
  return arr.reduce((acc, obj) => {
    const key = Object.keys(obj)[0]
    acc[key] = obj[key]
    return acc
  }, {})
}
